/**
 * Metrics Service — fetches the per-org performance summary from the backend.
 */

import axios from 'axios';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:14102/api/v1';
const API_BASE_URL = BASE_URL.replace(/\/api\/v1\/?$/, '');
const ACCESS_TOKEN_KEY = 'access_token';

function authHeader(): { Authorization: string } | undefined {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : undefined;
}

export interface RequestStats {
  count: number;
  error_count: number;
  error_rate: number;
  p50_ms: number;
  p95_ms: number;
  p99_ms: number;
}

export interface RoutePerf {
  route: string;
  count: number;
  p95_ms: number;
  error_count: number;
  error_rate: number;
}

export interface MinuteBucket {
  minute: number;
  count: number;
  errors: number;
}

export interface MetricsSummary {
  generated_at: string;
  organization_id: number;
  scope: 'system' | 'organization';
  uptime_seconds: number;
  process: {
    memory_heap_used_mb: number;
    memory_heap_total_mb: number;
    memory_rss_mb: number;
    node_version: string;
    pid: number;
    event_loop_lag_ms: number;
  };
  database: {
    healthy: boolean;
    latency_ms: number | null;
    pool_total: number;
    pool_idle: number;
    pool_waiting: number;
  };
  requests: {
    last_minute: RequestStats;
    last_5min: RequestStats;
    last_10min: RequestStats;
    per_minute: MinuteBucket[];
    top_routes: RoutePerf[];
  };
  events_last_hour: Record<string, number>;
  org_counters: {
    users_total: number;
    notifications_unread: number;
  };
}

export const metricsService = {
  async getSummary(): Promise<MetricsSummary> {
    const res = await axios.get(`${API_BASE_URL}/api/v1/metrics/summary`, {
      headers: authHeader(),
    });
    return res.data;
  },
};
